import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Communities", href: "#communities" },
  { label: "Executive Team", href: "#team" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while mobile drawer is open so touch scroll doesn't get trapped
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (href) => {
    const target = href === "#home" ? 0 : document.querySelector(href);
    // Close the mobile drawer first so its collapse animation doesn't
    // interfere with the scroll target computation on mobile browsers.
    setOpen(false);
    // Defer the scroll to the next frame so React can commit the close
    // and (on mobile) release the body overflow lock before scrolling.
    requestAnimationFrame(() => {
      if (lenis) {
        // Lenis handles smooth scroll natively; offset accounts for the
        // fixed glass header so the section title isn't hidden under it.
        lenis.scrollTo(target, { offset: -80, duration: 1.2 });
      } else if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (target === 0) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#F7F5F0]/80 backdrop-blur-xl border-b border-[#1C2220]/10 py-4"
          : "bg-transparent py-6"
      }`}
    >
      {!scrolled && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/45 to-transparent" />
      )}
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 flex items-center justify-between">
        <button
          onClick={() => go("#home")}
          data-testid="logo-button"
          className="flex items-center group"
        >
          <img
            src={scrolled ? "/federation-logo.png" : "/federation-logo-white.png"}
            alt="The Federation Companies"
            className="h-9 md:h-11 w-auto"
          />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.href.slice(1)}`}
              className={`relative text-sm transition-colors duration-300 group ${
                scrolled ? "text-[#1C2220]/70 hover:text-[#1C2220]" : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#C56A4E] group-hover:w-full transition-[width] duration-300" />
            </button>
          ))}
          <button
            onClick={() => go("#contact")}
            data-testid="header-cta"
            className={`rounded-sm text-sm px-5 py-2.5 hover:-translate-y-0.5 transition-[transform,background-color,color] duration-300 ${
              scrolled
                ? "bg-[#1C2220] text-white hover:bg-[#2A332F]"
                : "bg-white text-[#1C2220] hover:bg-[#F7F5F0]"
            }`}
          >
            Partner With Us
          </button>
        </nav>

        <button
          className={`md:hidden ${scrolled ? "text-[#1C2220]" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#F7F5F0]/95 backdrop-blur-xl border-b border-[#1C2220]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  data-testid={`mobile-nav-${l.href.slice(1)}`}
                  className="text-left font-heading text-2xl text-[#1C2220]"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
